// ─────────────────────────────────────────────────────────────────────
// EMOTICONS — Messenger-style auto-conversion of text smileys (:) ;) :D <3 …)
// to emoji while typing. Pure functions; the composers call autoEmojiAt on
// every change (converting only the token at the caret) and autoEmoji on
// send (converting whatever is left).
//
// Rules that keep it from mangling real text:
//   • A smiley is only converted as a whole token — preceded by the start of
//     the text or whitespace/an opening bracket, and followed by the end,
//     whitespace or punctuation. «http://» never converts (no boundary before
//     the colon), «:)))» converts the first «:)» only when followed by a
//     boundary … so it does not.
//   • Smileys that END IN A LETTER (:D :P :o xD B)) convert only once a
//     boundary follows — otherwise «:Dad» would become «😃ad» mid-word.
//     Punctuation-ended ones (:) :( ;) <3 :* :|) convert immediately.
// ─────────────────────────────────────────────────────────────────────

/** Text smiley → emoji. Order matters: longer forms first so «:-)» wins over «:)». */
export const EMOTICONS: ReadonlyArray<readonly [string, string]> = [
  [":'(", '😭'],
  [':-)', '😊'],
  [':-(', '😢'],
  [':-D', '😃'],
  [':-P', '😛'],
  [':-p', '😛'],
  [';-)', '😉'],
  [':-*', '😘'],
  [':-|', '😐'],
  [':-/', '😕'],
  [':-o', '😮'],
  [':-O', '😮'],
  ['(y)', '👍'],
  ['(Y)', '👍'],
  ['(n)', '👎'],
  ['(N)', '👎'],
  [':)', '😊'],
  [':(', '😢'],
  [':D', '😃'],
  [':P', '😛'],
  [':p', '😛'],
  [';)', '😉'],
  [':*', '😘'],
  [':|', '😐'],
  [':/', '😕'],
  [':o', '😮'],
  [':O', '😮'],
  ['<3', '❤️'],
  ['</3', '💔'],
  ['xD', '😂'],
  ['XD', '😂'],
  ['B)', '😎'],
  ['8)', '😎'],
];

const BOUNDARY_BEFORE = /[\s([{"'«]$/;
const BOUNDARY_AFTER = /^([\s.,!?;:)\]}"'»]|$)/;
const endsInLetter = (s: string) => /[A-Za-z]$/.test(s);

/** Is `text[at..]` a smiley token here? Returns the match or null. */
function matchAt(text: string, at: number, final: boolean): readonly [string, string] | null {
  if (at > 0 && !BOUNDARY_BEFORE.test(text.slice(Math.max(0, at - 1), at))) return null;
  for (const pair of EMOTICONS) {
    const [smiley] = pair;
    if (!text.startsWith(smiley, at)) continue;
    const after = text.slice(at + smiley.length);
    if (!BOUNDARY_AFTER.test(after)) continue;
    // Letter-ended smileys need a real boundary after them unless we are finalising.
    if (endsInLetter(smiley) && after === '' && !final) continue;
    return pair;
  }
  return null;
}

/** Convert every whole-token smiley in the text (used on send). */
export function autoEmoji(text: string): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    const m = matchAt(text, i, true);
    if (m) {
      out += m[1];
      i += m[0].length;
    } else {
      out += text[i];
      i += 1;
    }
  }
  return out;
}

/**
 * Convert the smiley the user just finished typing — the token that ends at
 * the caret, or the one right before a boundary character the caret follows.
 * Returns the new text and caret so the composer can keep the cursor in place.
 */
export function autoEmojiAt(text: string, caret: number): { text: string; caret: number } {
  const c = Math.max(0, Math.min(caret, text.length));
  return convertAt(text, c) ?? { text, caret: c };
}

function convertAt(text: string, c: number): { text: string; caret: number } | null {
  const head = text.slice(0, c);
  // Case 1: the caret sits right after a separator (space, newline, . , ! ?) →
  // the token before it. Brackets are not separators here: «:)» ends in one.
  const boundary = /[\s.,!?]$/.test(head) && head.length > 0;
  const tokenEnd = boundary ? c - 1 : c;
  // Walk back to the start of the token (a smiley is at most 3 chars).
  for (let start = Math.max(0, tokenEnd - 3); start <= tokenEnd; start++) {
    const m = matchAt(text, start, boundary);
    if (!m || start + m[0].length !== tokenEnd) continue;
    const next = text.slice(0, start) + m[1] + text.slice(tokenEnd);
    return { text: next, caret: c - m[0].length + m[1].length };
  }
  return null;
}
